import { useState, useEffect, useRef, useCallback } from "preact/hooks";
import { initialState, applyEvent, resolveDisplayName } from "./state";
import type { State } from "./state";
import type { MeetdayConfig } from "./types";
import { getUserAvatar } from "./avatars";
import { HeaderCalendar } from "./components/Header";
import CalendarMonth from "./components/CalendarMonth";
import SlotsPanel from "./components/SlotsPanel";
import ChatPanel from "./components/ChatPanel";
import SettingsDialog from "./components/SettingsDialog";
import BrutalModal from "./components/BrutalModal";
import { putEvent, markRead, setOnRateLimited, setOnEventTooLarge } from "./api";

type ConnectionStatus = "connecting" | "connected" | "disconnected" | "reconnecting";

/** PWA install prompt (Chromium). Not in all browsers. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const MONTH_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => ({
  value: m,
  label: new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" }),
}));

interface MonthNavProps {
  month: number;
  year: number;
  onPrev: () => void;
  onNext: () => void;
  onMonthChange: (e: Event) => void;
  onYearChange: (e: Event) => void;
}

const selectClass =
  "brutal-input rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-[2.5rem] px-2 py-1.5 text-sm sm:text-base font-semibold w-full min-w-0";

function MonthNav({
  month,
  year,
  onPrev,
  onNext,
  onMonthChange,
  onYearChange,
}: MonthNavProps) {
  const years: number[] = [];
  for (let y = year - 5; y <= year + 5; y += 1) {
    years.push(y);
  }
  return (
    <nav className="flex flex-nowrap items-center gap-2 sm:gap-3 mt-3 border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 px-2 sm:px-4 py-2">
      <button
        type="button"
        className="shrink-0 inline-flex items-center justify-center w-10 h-10 sm:min-w-[3rem] sm:px-4 sm:py-3 text-xl rounded-none border-2 border-black dark:border-gray-100 bg-brutal-purple text-white hover:bg-brutal-purple-hover"
        aria-label="Previous month"
        onClick={onPrev}
      >
        ←
      </button>
      <div className="flex flex-1 items-center justify-center gap-2 min-w-0">
        <select
          className={`${selectClass} flex-1 max-w-[8rem] sm:max-w-[10rem]`}
          value={month}
          onChange={onMonthChange}
          aria-label="Month"
        >
          {MONTH_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className={`${selectClass} w-auto max-w-[5rem] sm:max-w-[6rem]`}
          value={year}
          onChange={onYearChange}
          aria-label="Year"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="shrink-0 inline-flex items-center justify-center w-10 h-10 sm:min-w-[3rem] sm:px-4 sm:py-3 text-xl rounded-none border-2 border-black dark:border-gray-100 bg-brutal-purple text-white hover:bg-brutal-purple-hover"
        aria-label="Next month"
        onClick={onNext}
      >
        →
      </button>
    </nav>
  );
}

interface AppProps {
  config: MeetdayConfig;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const MAX_RECONNECT_ATTEMPTS = 10;
const RECENT_CALENDARS_KEY = "meetday_recent_calendars";

interface RecentCalendar {
  slug: string;
  name?: string;
  lastVisited: number;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Encode ArrayBuffer to base64url (no padding) for push subscription keys. */
function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer || buffer.byteLength === 0) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function updateRecentCalendars(slug: string, name?: string | null) {
  if (typeof window === "undefined" || !slug) return;
  try {
    const raw = window.localStorage.getItem(RECENT_CALENDARS_KEY);
    const list: RecentCalendar[] = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    const cutoff = now - 1000 * 60 * 60 * 24 * 120; // ~120 days
    const filtered = list.filter(
      (item) => item && item.slug && item.slug !== slug && item.lastVisited >= cutoff,
    );
    const entry: RecentCalendar = {
      slug,
      lastVisited: now,
    };
    if (name && name.trim()) {
      entry.name = name.trim();
    }
    filtered.push(entry);
    filtered.sort((a, b) => b.lastVisited - a.lastVisited);
    const trimmed = filtered.slice(0, 10);
    window.localStorage.setItem(RECENT_CALENDARS_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors.
  }
}

export default function App({ config }: AppProps) {
  const [state, setState] = useState<State>(initialState);
  const [selectedDate, setSelectedDate] = useState(config.selectedDate ?? "");
  const [viewMonth, setViewMonth] = useState(config.month);
  const [viewYear, setViewYear] = useState(config.year);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connecting");
  const lastEventIdRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);
  const username = resolveDisplayName(
    config.username ?? "",
    state.displayNameChain ?? undefined
  );
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(`meetday_push_${config.slug}`) === "on";
    } catch {
      return false;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [eventTooLargeMessage, setEventTooLargeMessage] = useState<string | null>(null);
  const rateLimitIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rateLimitRetryAtRef = useRef<number>(0);
  const eventTooLargeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setOnEventTooLarge(() => {
      if (eventTooLargeTimeoutRef.current) clearTimeout(eventTooLargeTimeoutRef.current);
      setEventTooLargeMessage("Message too long. Please shorten it.");
      eventTooLargeTimeoutRef.current = setTimeout(() => {
        eventTooLargeTimeoutRef.current = null;
        setEventTooLargeMessage(null);
      }, 5000);
    });
    return () => {
      setOnEventTooLarge(null);
      if (eventTooLargeTimeoutRef.current) clearTimeout(eventTooLargeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setOnRateLimited((retryAfterSeconds) => {
      if (rateLimitIntervalRef.current) {
        clearInterval(rateLimitIntervalRef.current);
        rateLimitIntervalRef.current = null;
      }
      const now = Date.now();
      rateLimitRetryAtRef.current = now + retryAfterSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((rateLimitRetryAtRef.current - Date.now()) / 1000));
        if (remaining <= 0) {
          if (rateLimitIntervalRef.current) {
            clearInterval(rateLimitIntervalRef.current);
            rateLimitIntervalRef.current = null;
          }
          setRateLimitMessage(null);
          return;
        }
        setRateLimitMessage(`Sending too quickly, retrying in ${remaining}s…`);
      };

      tick();
      rateLimitIntervalRef.current = setInterval(tick, 1000);
    });
    return () => {
      setOnRateLimited(null);
      if (rateLimitIntervalRef.current) {
        clearInterval(rateLimitIntervalRef.current);
        rateLimitIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const checkStandalone = () =>
      setIsStandalone(
        window.matchMedia("(display-mode: standalone)").matches ||
          (navigator as Navigator & { standalone?: boolean }).standalone === true
      );
    checkStandalone();
    window.matchMedia("(display-mode: standalone)").addEventListener("change", checkStandalone);
    return () =>
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", checkStandalone);
  }, []);

  useEffect(() => {
    const handleInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
  }, []);

  const updateMonthYear = (month: number, year: number) => {
    setViewMonth(month);
    setViewYear(year);
    setSelectedDate("");
    const params = new URLSearchParams(window.location.search);
    params.set("month", String(month));
    params.set("year", String(year));
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const goPrevMonth = () => {
    let m = viewMonth - 1;
    let y = viewYear;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    updateMonthYear(m, y);
  };

  const goNextMonth = () => {
    let m = viewMonth + 1;
    let y = viewYear;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    updateMonthYear(m, y);
  };

  const handleMonthChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const m = Number(target?.value) || viewMonth;
    updateMonthYear(m, viewYear);
  };

  const handleYearChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const y = Number(target?.value) || viewYear;
    updateMonthYear(viewMonth, y);
  };

  const calendarConfig = {
    ...config,
    month: viewMonth,
    year: viewYear,
  };

  lastEventIdRef.current = state.lastEventId;

  const fetchEvents = useCallback(
    (sinceId = 0) => {
      const url =
        sinceId > 0
          ? `/c/${config.slug}/events?since_id=${sinceId}`
          : `/c/${config.slug}/events`;
      return fetch(url)
        .then((r) => r.json())
        .then((events: unknown[]) => {
          const evList = events as Array<{ id?: number }>;
          const maxId = evList.length > 0
            ? Math.max(...evList.map((e) => e.id ?? 0))
            : 0;
          if (sinceId > 0) {
            setState((s) => {
              let next = s;
              for (const ev of events as Parameters<typeof applyEvent>[1][]) {
                next = applyEvent(next, ev, { username });
              }
              return next;
            });
          } else {
            let s = initialState();
            events.forEach((ev) => {
              s = applyEvent(s, ev as Parameters<typeof applyEvent>[1], {
                username,
              });
            });
            setState(s);
          }
          if (maxId > 0) {
            markRead(config.slug, maxId).catch(() => {});
          }
        })
        .catch(console.error);
    },
    [config.slug, username]
  );

  useEffect(() => {
    fetchEvents().catch(console.error);
  }, [fetchEvents]);

  useEffect(() => {
    const connect = () => {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/c/${config.slug}/ws`;
      const sock = new WebSocket(url);
      socketRef.current = sock;

      sock.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionStatus("connected");
        const sinceId = lastEventIdRef.current;
        if (sinceId > 0) {
          fetchEvents(sinceId);
        }
      };

      sock.onmessage = (e) => {
        try {
          const { kind, event } = JSON.parse(e.data as string);
          if (kind === "event" && event) {
            setState((s) => applyEvent(s, event, { username }));
            const id = event?.id;
            if (typeof id === "number" && id > 0) {
              markRead(config.slug, id).catch(() => {});
            }
          }
        } catch (err) {
          console.error(err);
        }
      };

      sock.onclose = () => {
        socketRef.current = null;
        setConnectionStatus("disconnected");
        const attempt = reconnectAttemptRef.current;
        if (attempt < MAX_RECONNECT_ATTEMPTS) {
          setConnectionStatus("reconnecting");
          const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
          reconnectAttemptRef.current = attempt + 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
          }, delay);
        }
      };

      sock.onerror = () => {
        sock.close();
      };
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      const sock = socketRef.current;
      if (sock) {
        sock.onclose = null;
        sock.close();
        socketRef.current = null;
      }
    };
  }, [config.slug, username, fetchEvents]);

  const shareURL =
    typeof window !== "undefined"
      ? `${window.location.origin}/c/${config.slug}`
      : "";

  const calendarTitle = state.calendarName || config.slug;

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = calendarTitle ? `${calendarTitle} – Meetday` : "Meetday";
    }
  }, [calendarTitle]);

  // Record this calendar in the local recent-calendars list.
  useEffect(() => {
    updateRecentCalendars(config.slug, state.calendarName ?? null);
  }, [config.slug, state.calendarName]);

  /** Returns so the dialog can know not to update state on failure; throws on error so caller can catch. */
  async function enableNotifications(): Promise<void> {
    if (typeof window === "undefined") throw new Error("Not in browser");
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      throw new Error("Notifications or Service Worker not supported");
    }
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") throw new Error("Notification permission denied");

    const registration = await navigator.serviceWorker.ready;
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      const vapidKey = (config as any).vapidPublicKey as string | undefined;
      if (!vapidKey || !String(vapidKey).trim()) {
        throw new Error(
          "Push notifications are not configured on this server. The server administrator must set MEETDAY_VAPID_PUBLIC and MEETDAY_VAPID_PRIVATE environment variables."
        );
      }
      const appServerKey = urlBase64ToUint8Array(vapidKey);
      const appServerKeyBuffer = appServerKey.buffer as ArrayBuffer;
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKeyBuffer,
      });
    }
    // Prefer toJSON() (works in Chrome); fall back to getKey() for keys.
    let p256dhStr = "";
    let authStr = "";
    const j = typeof (sub as any).toJSON === "function" ? (sub as any).toJSON() : null;
    if (j && j.keys) {
      p256dhStr = j.keys.p256dh ?? "";
      authStr = j.keys.auth ?? "";
    }
    if ((!p256dhStr || !authStr) && sub.getKey) {
      const p256 = sub.getKey("p256dh");
      const a = sub.getKey("auth");
      if (p256) p256dhStr = arrayBufferToBase64Url(p256);
      if (a) authStr = arrayBufferToBase64Url(a);
    }
    if (!p256dhStr || !authStr) {
      console.warn("Push subscription missing keys (p256dh/auth)");
      throw new Error("Could not read subscription keys");
    }
    const body = { endpoint: sub.endpoint, keys: { p256dh: p256dhStr, auth: authStr } };
    const res = await fetch(`/c/${config.slug}/push/subscribe`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      console.warn("Push subscribe failed:", res.status, text);
      throw new Error(`Subscribe failed: ${res.status}`);
    }
    try {
      window.localStorage.setItem(`meetday_push_${config.slug}`, "on");
    } catch {
      // ignore
    }
    setNotificationsEnabled(true);
  }

  async function disableNotifications() {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      const payload = sub ? { endpoint: sub.endpoint } : {};
      const res = await fetch(`/c/${config.slug}/push/unsubscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.warn("Push unsubscribe failed:", res.status, await res.text());
      }
      try {
        window.localStorage.setItem(`meetday_push_${config.slug}`, "off");
      } catch {
        // ignore
      }
      setNotificationsEnabled(false);
    } catch {
      // Ignore failures; user can retry.
    }
  }

  const openRenameModal = () => {
    setRenameValue(state.calendarName || config.slug);
    setRenameOpen(true);
  };

  const handleRenameSubmit = (e: Event) => {
    e.preventDefault();
    const nextName = renameValue.trim();
    if (!nextName) {
      setRenameOpen(false);
      return;
    }
    const message = username
      ? `${username} renamed the calendar to "${nextName}".`
      : `Calendar renamed to "${nextName}".`;
    void putEvent(config.slug, {
      type: "calendar_renamed",
      message,
      data: { name: nextName },
    }).finally(() => {
      setRenameOpen(false);
    });
  };

  return (
    <>
      <BrutalModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename calendar"
        maxWidth="max-w-sm"
      >
        <form onSubmit={handleRenameSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm text-gray-900 dark:text-gray-100">
            <span>Calendar name</span>
            <input
              type="text"
              className="brutal-input px-3 py-2 rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={renameValue}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                setRenameValue(target.value);
              }}
              maxLength={120}
              autoFocus
            />
          </label>
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              className="inline-flex items-center justify-center min-h-[2.25rem] px-4 py-1.5 text-sm rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900"
              onClick={() => setRenameOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center min-h-[2.25rem] px-4 py-1.5 text-sm rounded-none border-2 border-black dark:border-gray-100 bg-brutal-purple text-white hover:bg-brutal-purple-hover"
            >
              Save
            </button>
          </div>
        </form>
      </BrutalModal>
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        slug={config.slug}
        username={username}
        avatar={getUserAvatar(config.username ?? "", state.avatarsByUsername)}
        notificationsEnabled={notificationsEnabled}
        notificationsSupported={Boolean(String((config as any).vapidPublicKey ?? "").trim())}
        onEnableNotifications={enableNotifications}
        onDisableNotifications={disableNotifications}
        onNotificationsChange={setNotificationsEnabled}
        installPrompt={installPrompt}
        isStandalone={isStandalone}
      />
      <div className="flex flex-1 min-h-0 flex-col w-full">
        <HeaderCalendar
          slug={config.slug}
          calendarName={state.calendarName ?? null}
          avatar={getUserAvatar(config.username ?? "", state.avatarsByUsername)}
          username={username}
          shareURL={shareURL}
          onEditCalendarName={openRenameModal}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      <div className="relative flex flex-1 min-h-0 flex-col w-full pt-6 px-6 pb-10 max-w-4xl mx-auto gap-4 lg:flex-row lg:items-stretch lg:gap-6">
        {(connectionStatus === "disconnected" || connectionStatus === "reconnecting") && (
          <div
            className="absolute top-0 left-0 right-0 z-10 px-4 py-2 text-center text-sm font-semibold bg-orange-200 dark:bg-orange-900/70 text-black dark:text-orange-100 border-b-4 border-orange-500 dark:border-orange-500"
            role="status"
            aria-live="polite"
          >
            {connectionStatus === "reconnecting"
              ? "Disconnected. Reconnecting…"
              : "Disconnected"}
          </div>
        )}
        {(rateLimitMessage || eventTooLargeMessage) && (
          <div
            className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-20 px-3 py-2 text-xs rounded-none border-2 shadow-lg ${
              eventTooLargeMessage
                ? "font-semibold text-white bg-brutal-purple border-2 border-black dark:border-gray-100"
                : "text-gray-700 dark:text-gray-300 bg-white/95 dark:bg-gray-800/95 border-2 border-gray-400 dark:border-gray-500"
            }`}
            role="status"
            aria-live="polite"
          >
            {rateLimitMessage ?? eventTooLargeMessage}
          </div>
        )}
        <div
          className={`flex flex-1 min-h-0 gap-4 w-full flex-col lg:flex-row ${
            connectionStatus === "disconnected" || connectionStatus === "reconnecting"
              ? "pt-12"
              : ""
          }`}
        >
          <div className="flex flex-col flex-1 min-w-0 gap-4 order-2 lg:order-1 lg:flex-[0_0_55%] lg:min-h-0 lg:max-w-[800px] lg:w-[800px]">
            <section className="flex-shrink-0 min-w-0" aria-label="Calendar">
          <MonthNav
            month={viewMonth}
            year={viewYear}
            onPrev={goPrevMonth}
            onNext={goNextMonth}
            onMonthChange={handleMonthChange}
            onYearChange={handleYearChange}
          />
          <CalendarMonth
            state={state}
            config={calendarConfig}
            selectedDate={selectedDate}
            onDayClick={setSelectedDate}
          />
            </section>
            <section
              className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden"
              aria-label="Slots"
            >
              <SlotsPanel
            state={state}
            config={calendarConfig}
            slug={config.slug}
            selectedDate={selectedDate}
            username={username}
              />
            </section>
          </div>
          <aside
            className="min-w-0 order-1 lg:order-2 lg:flex-shrink-0 lg:w-80"
            aria-label="Chat and activity log"
          >
            <ChatPanel
              state={state}
              slug={config.slug}
              username={username}
              lastReadEventId={config.lastReadEventId}
            />
          </aside>
        </div>
      </div>
      </div>
    </>
  );
}
